/**
 * Who is presenting something right now, for the "Presenting" badge on tiles.
 *
 * The badge used to read `participant.isScreenSharing`, which is true only for
 * a remote peer whose screen stream has arrived. Opening the whiteboard, or
 * presenting a document or a video, put the content on everyone's stage with
 * nothing on the presenter's own tile to say whose it was. Every kind of
 * presentation counts here, and so does your own: the tile reads the same
 * whoever is looking at it.
 *
 * Kept apart from `isScreenSharing`, which also decides whether a moderator is
 * offered "Stop screen share" — an action that means nothing for a whiteboard.
 */

export type PresentingSources = {
  /** Screen shares on the stage. Your own has `isLocal` and no peer id. */
  screens: { key: string; isLocal: boolean }[]
  /** peerId of whoever opened the whiteboard, or null when none is open. */
  whiteboardBy: string | null
  /**
   * Documents and videos on the stage. `by` is null only for a share of
   * yours the room has not echoed back yet (`local`).
   */
  media: { by: string | null; local?: boolean }[]
  localPeerId: string | null
}

/**
 * Every presenter's peer id, once each, sorted — so the result can be compared
 * as a string and does not churn when the same people stay on the stage.
 */
export function presentingPeerIds({
  screens,
  whiteboardBy,
  media,
  localPeerId,
}: PresentingSources): string[] {
  const ids = new Set<string>()
  // A remote screen share is keyed by its sharer's peer id; your own is 'local'.
  for (const s of screens) {
    const id = s.isLocal ? localPeerId : s.key
    if (id) ids.add(id)
  }
  if (whiteboardBy) ids.add(whiteboardBy)
  for (const m of media) {
    const id = m.by ?? (m.local ? localPeerId : null)
    if (id) ids.add(id)
  }
  return [...ids].sort()
}
